<template>
  <article v-if="machine" class="job-dialog-machine-item">
    <button type="button" class="machine-summary" @click="emitOpen">
      <div class="machine-image">
        <MachineModelImage :size="52" :relativePath="machineImage" />
      </div>

      <div class="machine-main">
        <div class="machine-title-row">
          <h3>{{ machineTitle }}</h3>
          <span v-if="machine.IsActive === false" class="state-chip">
            <v-icon size="14">mdi-pause-circle</v-icon>
            {{ t("text.inactive") }}
          </span>
        </div>

        <p>{{ machineSubtitle }}</p>

        <div v-if="machineFacts.length" class="machine-fact-row">
          <span v-for="fact in machineFacts" :key="fact.label" class="machine-fact">
            <v-icon size="15">{{ fact.icon }}</v-icon>
            <span>{{ fact.value }}</span>
          </span>
        </div>
      </div>

      <span class="open-button" :title="openLabel" :aria-label="openLabel">
        <v-icon size="20">mdi-chevron-right</v-icon>
      </span>
    </button>

    <div v-if="commentSections.length || orderLines.length" class="machine-details">
      <div v-if="commentSections.length" class="machine-comments">
        <section v-for="comment in commentSections" :key="comment.label" class="machine-comment">
          <div class="comment-label">{{ comment.label }}</div>
          <div class="comment-box">{{ comment.value }}</div>
        </section>
      </div>

      <div v-if="orderLines.length" class="machine-order-lines" :class="{ open: articlesOpen }">
        <button type="button" class="articles-toggle" :aria-expanded="articlesOpen" @click="articlesOpen = !articlesOpen">
          <v-icon size="16" class="articles-icon">mdi-package-variant-closed</v-icon>
          <span class="articles-label">{{ t("text.articles") }}</span>
          <span class="articles-count">{{ orderLines.length }}</span>
          <span class="articles-chevron" :class="{ open: articlesOpen }">
            <v-icon size="18">mdi-chevron-down</v-icon>
          </span>
        </button>
        <table v-if="articlesOpen" class="order-line-table">
          <thead>
            <tr>
              <th class="article-number">{{ t("text.article") }}</th>
              <th></th>
              <th class="num">{{ t("text.amount") }}</th>
              <th v-if="pricesVisible" class="num">{{ t("text.price") }}</th>
              <th v-if="pricesVisible" class="num">{{ t("text.sum") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in orderLines" :key="line.Id">
              <td class="article-number">{{ line.ArticleNumber }}</td>
              <td class="line-desc">
                <span class="line-name">{{ line.ArticleDescription }}</span>
              </td>
              <td class="num">{{ formatQuantity(line.ArticleCount) }}</td>
              <td v-if="pricesVisible" class="num">{{ formatMoney(line.UnitPrice) }}</td>
              <td v-if="pricesVisible" class="num">{{ formatMoney(line.LineTotal) }}</td>
            </tr>
          </tbody>
          <tfoot v-if="pricesVisible">
            <tr>
              <td colspan="4" class="num total-label">{{ t("text.sum") }}</td>
              <td class="num total-value">{{ formatMoney(orderTotal) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from "vue";
import MachineModelImage from "@/components/Cards/MachineModelImage.vue";
import { formatCurrency } from "@/helpers/appGlobals";

defineOptions({ name: "JobDialogMachineItem" });

const props = defineProps({
  machineJob: {
    type: Object,
    default: null,
  },
  showPrice: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["open"]);
const instance = getCurrentInstance();
const articlesOpen = ref(false);

const machine = computed(() => props.machineJob?.Machine || null);
const machineImage = computed(() => machine.value?.ModelImage || machine.value?.MachineModel?.ModelImage || undefined);

const machineTitle = computed(() => {
  const item = machine.value;
  if (!item) return "";

  return item.DisplayName
    || [item.MachineModelName, item.Name].filter(Boolean).join(" ")
    || t("text.equipment");
});

const machineSubtitle = computed(() => {
  const item = machine.value;
  if (!item) return "";

  const values = [
    item.SupplierName,
    nameFromValue(item.MachineType),
    item.MachineModelName || item.MachineModel?.Name,
  ]
    .filter(Boolean)
    .filter((value, index, array) => array.indexOf(value) === index);

  return values.length ? values.join(" / ") : "-";
});

const machineFacts = computed(() => {
  const item = machine.value;
  if (!item) return [];

  return [
    { label: "serial", icon: "mdi-barcode", value: item.SerialNumber },
    { label: "qr", icon: "mdi-qrcode", value: item.Qrcode },
  ].filter((fact) => Boolean(fact.value));
});

const orderLines = computed(() => {
  const lines = props.machineJob?.OrderLines;
  return Array.isArray(lines) ? lines : [];
});

const pricesVisible = computed(() =>
  props.showPrice && orderLines.value.some((line) => line.UnitPrice != null || line.LineTotal != null)
);

const orderTotal = computed(() =>
  orderLines.value.reduce((sum, line) => sum + (Number(line.LineTotal) || 0), 0)
);

const commentSections = computed(() => {
  const item = props.machineJob || {};
  return [
    { label: t("text.error_description"), value: item.CustomerComment },
    { label: t("text.invocie_comment"), value: item.InvoiceComment },
  ].filter((comment) => Boolean(comment.value));
});

const openLabel = computed(() => [t("text.open"), machineTitle.value].filter(Boolean).join(" "));

function t(key) {
  return instance?.proxy?.$t ? instance.proxy.$t(key) : key;
}

function nameFromValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.Name || value.DisplayName || "";
}

function formatQuantity(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return "";
  return Number.isInteger(number)
    ? String(number)
    : number.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatMoney(value) {
  if (value == null || Number.isNaN(Number(value))) return "";
  return formatCurrency(Number(value));
}

function emitOpen() {
  if (machine.value) {
    emit("open", machine.value);
  }
}
</script>

<style lang="scss" scoped>
.job-dialog-machine-item {
  background: #fff;
  border: 1px solid rgba(24, 39, 75, 0.1);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.job-dialog-machine-item:hover,
.job-dialog-machine-item:focus-within {
  border-color: rgba(30, 116, 92, 0.3);
  box-shadow: 0 10px 24px rgba(24, 39, 75, 0.08);
  transform: translateY(-1px);
}

.machine-summary {
  align-items: center;
  background: #fff;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: grid;
  font: inherit;
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-width: 0;
  padding: 12px;
  text-align: left;
  width: 100%;
}

.machine-summary:focus-visible {
  outline: 2px solid rgba(30, 116, 92, 0.28);
  outline-offset: -2px;
}

.machine-image {
  align-items: center;
  background: #f6f8fa;
  border: 1px solid rgba(24, 39, 75, 0.08);
  border-radius: 8px;
  display: flex;
  height: 62px;
  justify-content: center;
  width: 62px;
}

.machine-main {
  min-width: 0;
}

.machine-title-row {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  min-width: 0;
}

.machine-title-row h3 {
  color: rgba(25, 34, 46, 0.92);
  display: -webkit-box;
  font-size: 15px;
  font-weight: 750;
  line-clamp: 2;
  line-height: 1.28;
  margin: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.machine-main p {
  color: rgba(25, 34, 46, 0.58);
  font-size: 12px;
  line-height: 1.35;
  margin: 4px 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.state-chip,
.machine-fact {
  align-items: center;
  border-radius: 6px;
  display: inline-flex;
  gap: 5px;
  min-width: 0;
}

.state-chip {
  background: #f2f4f7;
  color: rgba(25, 34, 46, 0.62);
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 7px;
}

.machine-fact-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.machine-fact {
  background: #f8fafc;
  border: 1px solid rgba(24, 39, 75, 0.08);
  color: rgba(25, 34, 46, 0.64);
  font-size: 12px;
  max-width: 210px;
  padding: 4px 7px;
}

.machine-fact span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.open-button {
  align-items: center;
  background: #f4f7f9;
  border-radius: 50%;
  color: rgba(30, 116, 92, 0.9);
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.machine-details {
  border-top: 1px solid rgba(24, 39, 75, 0.08);
  display: grid;
  gap: 10px;
  padding: 10px 12px 12px;
}

.machine-order-lines {
  min-width: 0;
  overflow-x: auto;
}

.articles-toggle {
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(24, 39, 75, 0.12);
  border-radius: 7px;
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: 8px;
  padding: 7px 8px 7px 10px;
  text-align: left;
  width: 100%;
}

.articles-toggle:hover,
.machine-order-lines.open .articles-toggle {
  background: #f3f7f5;
  border-color: rgba(30, 116, 92, 0.35);
}

.articles-toggle:focus-visible {
  outline: 2px solid rgba(30, 116, 92, 0.28);
  outline-offset: 2px;
}

.articles-icon {
  color: rgba(30, 116, 92, 0.9);
}

.articles-label {
  color: rgba(25, 34, 46, 0.78);
  font-size: 12px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.articles-count {
  background: #fff;
  border: 1px solid rgba(24, 39, 75, 0.1);
  border-radius: 999px;
  color: rgba(25, 34, 46, 0.78);
  font-size: 12px;
  font-weight: 750;
  line-height: 1;
  margin-left: auto;
  min-width: 22px;
  padding: 4px 7px;
  text-align: center;
}

.articles-chevron {
  align-items: center;
  background: #f4f7f9;
  border-radius: 50%;
  color: rgba(30, 116, 92, 0.9);
  display: inline-flex;
  height: 26px;
  justify-content: center;
  transition: transform 0.16s ease;
  width: 26px;
}

.articles-chevron.open {
  transform: rotate(180deg);
}

.machine-order-lines.open .articles-toggle {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.order-line-table {
  background: #fff;
  border: 1px solid rgba(30, 116, 92, 0.35);
  border-collapse: collapse;
  border-top: 0;
  border-bottom-left-radius: 7px;
  border-bottom-right-radius: 7px;
  width: 100%;
}

.order-line-table th,
.order-line-table td {
  padding: 4px 10px;
  vertical-align: middle;
}

.order-line-table th {
  color: rgba(25, 34, 46, 0.5);
  font-size: 11px;
  font-weight: 750;
  text-align: left;
  text-transform: uppercase;
}

.order-line-table td {
  border-top: 1px solid rgba(24, 39, 75, 0.06);
  color: rgba(25, 34, 46, 0.78);
  font-size: 13px;
}

.order-line-table .article-number {
  white-space: nowrap;
}

.order-line-table .line-desc {
  max-width: 0;
  width: 100%;
}

.order-line-table .num {
  text-align: right;
  white-space: nowrap;
}

.line-name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.total-label,
.total-value {
  font-weight: 750;
}

.machine-comments {
  display: grid;
  gap: 8px;
}

.comment-label {
  color: rgba(25, 34, 46, 0.58);
  font-size: 11px;
  font-weight: 750;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.comment-box {
  background: #f8fafc;
  border: 1px solid rgba(24, 39, 75, 0.08);
  border-radius: 7px;
  color: rgba(25, 34, 46, 0.78);
  font-size: 13px;
  line-height: 1.45;
  max-height: 120px;
  overflow-y: auto;
  padding: 9px 10px;
  white-space: pre-wrap;
}

@media (max-width: 600px) {
  .machine-summary {
    align-items: flex-start;
    gap: 10px;
    grid-template-columns: auto minmax(0, 1fr);
    padding: 10px;
  }

  .machine-image {
    height: 54px;
    width: 54px;
  }

  .open-button {
    display: none;
  }

  .machine-fact {
    max-width: 100%;
  }
}
</style>
